import { CheckCircle, ArrowRight, TrendingUp, MessageSquare, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const setupTasks = [
  { name: "Bot Settings", path: "/settings" },
  { name: "Knowledge Base", path: "/knowledge" },
  { name: "Products & Services", path: "/products" },
  { name: "WhatsApp Link", path: "/whatsapp" },
];

export default function Dashboard() {
  const [completed, setCompleted] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMessages, setTotalMessages] = useState(0);

  // Load completed tasks from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("taskProgress");
    if (saved) {
      const old = JSON.parse(saved);
      const valid = old.filter((path) =>
        setupTasks.some((task) => task.path === path)
      );
      setCompleted(valid);
      localStorage.setItem("taskProgress", JSON.stringify(valid));
    }
  }, []);

  // Fetch analytics from backend
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await fetch("https://zaichat.net/api/analytics", {
          headers: { auth: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Failed to fetch analytics");

        const data = await res.json();
        setWeeklyData(data.data.weekly_data || []);
        setHourlyData(data.data.hourly_data || []);
        setTotalMessages(data.data.total_messages || 0);
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setWeeklyData([]);
        setHourlyData([]);
        setTotalMessages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const progress = Math.min(
    100,
    Math.round((completed.length / setupTasks.length) * 100)
  );

  const peakDay =
    weeklyData.length > 0
      ? weeklyData.reduce((a, b) => (a.value > b.value ? a : b))
      : { name: "N/A", value: 0 };

  const peakHour =
    hourlyData.length > 0
      ? hourlyData.reduce((a, b) => (a.value > b.value ? a : b))
      : { hour: "N/A", value: 0 };

  const handleTaskClick = (path) => {
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 px-6 py-8 md:px-12 md:py-12">
      <div className="max-w-7xl mx-auto">

{/* HEADER */}
<motion.div 
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  className="mb-12"
>
  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
    Welcome Back
  </h1>
  <p className="text-lg text-gray-600">
    Complete your setup and start engaging with customers
  </p>
</motion.div>


        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{totalMessages}</h3>
            <p className="text-sm text-gray-500">Total Messages</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{completed.length}/{setupTasks.length}</h3>
            <p className="text-sm text-gray-500">Steps Completed</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{peakHour.hour}</h3>
            <p className="text-sm text-gray-500">Peak Hour</p>
          </motion.div>
        </div>

{/* SETUP STEPS */}
<motion.div 
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.4 }}
  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-12"
>
  <div className="mb-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-2">
      Setup Steps
    </h2>
    <p className="text-gray-600">
      Follow these steps to configure your chatbot
    </p>
  </div>

  <div className="space-y-3">
    {setupTasks.map((task, index) => (
      <motion.div
        key={task.path}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 + index * 0.1 }}
      >
        <button
          onClick={() => handleTaskClick(task.path)}
          className="w-full flex items-center justify-between p-5 rounded-xl border-2 
                     bg-gray-50 border-gray-200 hover:border-orange-300 
                     hover:shadow-md transition-all duration-300 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center 
                            text-lg font-bold bg-white text-gray-400 
                            border-2 border-gray-300">
              {index + 1}
            </div>

            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">
                {task.name}
              </h3>
              <p className="text-sm text-gray-500">
                Click to configure
              </p>
            </div>
          </div>

          <ArrowRight className="w-5 h-5 text-gray-400 
                                 group-hover:translate-x-1 
                                 group-hover:text-orange-500 
                                 transition-transform" />
        </button>
      </motion.div>
    ))}
  </div>
</motion.div>

        {/* ANALYTICS */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Analytics Insights
          </h2>
          <p className="text-gray-600 mb-8">
            Understand when your customers are most active
          </p>

          {loading && (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto rounded-full"></div>
              <p className="mt-4 text-gray-500">Loading analytics...</p>
            </div>
          )}

          {!loading && weeklyData.length === 0 && hourlyData.length === 0 && (
            <div className="text-center bg-white p-16 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 text-lg">
                No data yet. Start chatting to see analytics!
              </p>
            </div>
          )}

          {!loading && (weeklyData.length > 0 || hourlyData.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* WEEKLY */}
              {weeklyData.length > 0 && (
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <TrendingUp className="w-4 h-4" />
                    Peak: {peakDay.name} ({peakDay.value} messages)
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Weekly Distribution
                  </h3>

                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Tooltip />
                      <Pie
                        data={weeklyData}
                        dataKey="value"
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={3}
                        cx="50%"
                        cy="50%"
                      >
                        {weeklyData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* HOURLY */}
              {hourlyData.length > 0 && (
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <Clock className="w-4 h-4" />
                    Peak: {peakHour.hour} ({peakHour.value} messages)
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Hourly Distribution
                  </h3>

                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={hourlyData}>
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 11, fill: "#666" }}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#666" }} />
                      <Tooltip />
                      <Bar
                        dataKey="value"
                        fill="#F97316"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

            </div>
          )}
        </motion.div>

        {/* SUPPORT */}
{/* SUPPORT */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
  <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <h3 className="text-lg font-bold text-gray-900 mb-2">
      Documentation
    </h3>
    <p className="text-gray-600 text-sm">
      Learn how to configure your chatbot
    </p>
  </div>

  <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <h3 className="text-lg font-bold text-gray-900 mb-2">
      Video Tutorials
    </h3>
    <p className="text-gray-600 text-sm">
      Quick and helpful guides
    </p>
  </div>

  <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <h3 className="text-lg font-bold text-gray-900 mb-2">
      Need Help?
    </h3>
    <p className="text-gray-600 text-sm mb-3">
      Our support team is ready to assist
    </p>
    <a
      href="https://wa.me/970595546850"
      target="_blank"
      rel="noopener noreferrer"
      className="text-orange-500 font-semibold hover:text-orange-600"
    >
      Contact Support
    </a>
  </div>
</div>


        {/* FOOTER */}
        <div className="text-center text-gray-500 text-sm">
          Powered by <span className="text-orange-500 font-semibold">Zuccess AI</span>
        </div>
      </div>
    </div>
  );
}